import { useEffect, useState, useRef } from "react";
import { getNotifications, markAllNotificationsRead } from "../firebase/services";

const ICONS = {
  ticket_open: "🔧", ticket_update: "🔄",
  payment: "💳", payment_confirmed: "✅", payment_reminder: "🔔",
  message: "💬", default: "📢"
};

function timeAgo(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}j`;
}

// Demander la permission pour les notifications navigateur
async function requestBrowserNotifPermission() {
  if (!("Notification" in window)) return false;
  if (Notification.permission === "granted") return true;
  if (Notification.permission === "denied") return false;
  const result = await Notification.requestPermission();
  return result === "granted";
}

// Afficher une notification navigateur (fonctionne en arrière-plan)
function showBrowserNotif(title, body, type) {
  if (Notification.permission !== "granted") return;
  const icons = { ticket_open:"🔧", payment:"💳", message:"💬" };
  try {
    new Notification(title, {
      body,
      icon: "/logo192.png",
      badge: "/icons/icon-72x72.png",
      tag: type || "iam-notif",
    });
  } catch(e) {}
}

export default function NotificationBell({ userId, role, roomId }) {
  const [notifs, setNotifs]   = useState([]);
  const [open, setOpen]       = useState(false);
  const [permAsked, setPermAsked] = useState(false);
  const prevCountRef = useRef(0);

  // Demander permission au premier rendu
  useEffect(() => {
    if (!permAsked) {
      setPermAsked(true);
      requestBrowserNotifPermission();
    }
  }, [permAsked]);

  useEffect(() => {
    if (!userId) return;
    const unsub = getNotifications(userId, role, roomId, (data) => {
      // Détecter nouvelles notifs non lues → afficher notif navigateur
      const unreadNow = data.filter(n => !n.read).length;
      if (unreadNow > prevCountRef.current && prevCountRef.current >= 0) {
        const newest = data.find(n => !n.read);
        if (newest) {
          showBrowserNotif(newest.title, newest.body, newest.type);
        }
      }
      prevCountRef.current = unreadNow;
      setNotifs(data);
    });
    return unsub;
  }, [userId, role, roomId]);

  const unread = notifs.filter(n => !n.read).length;

  const handleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      try { await markAllNotificationsRead(roomId, role); } catch (e) {}
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <button style={S.bell} onClick={handleOpen} aria-label="Notifications">
        🔔
        {unread > 0 && <span style={S.dot}>{unread > 9 ? "9+" : unread}</span>}
      </button>

      {open && (
        <>
          <div style={S.overlay} onClick={() => setOpen(false)} />
          <div style={S.panel}>
            <div style={S.header}>
              <p style={S.title}>Notifications</p>
              <button style={S.close} onClick={() => setOpen(false)}>✕</button>
            </div>
            <div style={S.body}>
              {notifs.length === 0 ? (
                <div style={S.empty}>
                  <span style={{ fontSize: 28 }}>🔔</span>
                  <p style={{ fontSize: 13, color: "#7a90a8", marginTop: 8 }}>Aucune notification</p>
                </div>
              ) : notifs.slice(0, 30).map(n => (
                <div key={n.id} style={{ ...S.item, background: n.read ? "transparent" : "#f0f7ff" }}>
                  <span style={{ fontSize: 16, flexShrink: 0 }}>{ICONS[n.type] || ICONS.default}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={S.nTitle}>{n.title}</p>
                    <p style={S.nBody}>{n.body}</p>
                    <p style={S.nTime}>{timeAgo(n.createdAt)}</p>
                  </div>
                  {!n.read && <div style={S.unreadDot} />}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const S = {
  bell: { background: "rgba(255,255,255,0.15)", border: "none", fontSize: 18, cursor: "pointer", padding: "6px 8px", borderRadius: 8, position: "relative", color: "white" },
  dot:  { position: "absolute", top: -4, right: -4, width: 18, height: 18, borderRadius: "50%", background: "#e24b4a", color: "white", fontSize: 9, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" },
  overlay: { position: "fixed", inset: 0, zIndex: 998 },
  panel: { position: "absolute", top: 36, right: 0, width: 290, maxHeight: 400, background: "white", borderRadius: 14, boxShadow: "0 8px 30px rgba(0,0,0,0.15)", zIndex: 999, overflow: "hidden", border: "0.5px solid #d0dce8" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderBottom: "0.5px solid #e8f0f8" },
  title:  { fontSize: 14, fontWeight: 700, color: "#1a3a5c", margin: 0 },
  close:  { background: "none", border: "none", fontSize: 14, cursor: "pointer", color: "#7a90a8" },
  body:   { overflowY: "auto", maxHeight: 340 },
  empty:  { display: "flex", flexDirection: "column", alignItems: "center", padding: "24px 0" },
  item:   { display: "flex", alignItems: "flex-start", gap: 10, padding: "9px 14px", borderBottom: "0.5px solid #f0f4f8", cursor: "pointer" },
  nTitle: { fontSize: 12, fontWeight: 600, color: "#1a2a3a", margin: "0 0 2px" },
  nBody:  { fontSize: 11, color: "#5a7a9a", margin: "0 0 2px", lineHeight: 1.4 },
  nTime:  { fontSize: 10, color: "#aab8c8", margin: 0 },
  unreadDot: { width: 8, height: 8, borderRadius: "50%", background: "#2e86c1", flexShrink: 0, marginTop: 4 },
};
